import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  TextInput,
  Textarea,
  Stepper,
  NumberInput,
  Stack,
  Grid,
  Divider,
  Radio,
  ActionIcon,
  Alert,
  Badge,
} from "@mantine/core";
import {
  IconCheck,
  IconCalendar,
  IconUpload,
  IconTrash,
} from "@tabler/icons-react";
import { API_BASE_URL } from "../../config/baseUrls";

const ImageUpload = ({ onUpload, existingUrl, className }) => {
  const [preview, setPreview] = useState(existingUrl);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setPreview(existingUrl);
  }, [existingUrl]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        const res = await fetch(
          `${API_BASE_URL}/auth/uploadImage`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            },
            body: JSON.stringify({ image: base64, name: file.name }),
          },
        );
        const data = await res.json();
        if (data.url) {
          setPreview(data.url);
          onUpload(data.url);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setUploading(false);
      }
    };
  };

  return (
    <Box className={className}>
      {preview ? (
        <Box
          pos="relative"
          w="100%"
          h={200}
          bg="gray.1"
          style={{ borderRadius: 8, overflow: "hidden" }}
        >
          <img
            src={preview}
            alt="Upload"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <Group pos="absolute" bottom={10} right={10}>
            <Button component="label" size="xs" variant="white">
              Change{" "}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFile}
              />
            </Button>
            <ActionIcon
              color="red"
              variant="filled"
              onClick={() => {
                setPreview(null);
                onUpload("");
              }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Box>
      ) : (
        <Button
          component="label"
          variant="outline"
          color="gray"
          fullWidth
          h={120}
          style={{ borderStyle: "dashed" }}
          loading={uploading}
        >
          <Stack gap={0} align="center">
            <IconUpload size={24} />
            <Text size="sm" mt={4}>
              Click to Upload Image
            </Text>
          </Stack>
          <input type="file" hidden accept="image/*" onChange={handleFile} />
        </Button>
      )}
    </Box>
  );
};

export default function MegaQuizForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentStep, setCurrentStep] = useState(0);

  const [formData, setFormData] = useState({
    titleEn: "",
    titleJa: "",
    coverImg: "",
    startTime: "",
    endTime: "",
    totalQuestions: 50,
    pointsPerQuestion: 1,
    questions: Array(50)
      .fill(null)
      .map(() => ({
        questionEn: "",
        questionJa: "",
        mediaUrl: "",
        options: Array(4)
          .fill(null)
          .map(() => ({ optionEn: "", optionJa: "" })),
        correctAnswerIndex: 0,
      })),
  });

  const updateFormData = (key, val) => {
    setFormData((prev) => ({ ...prev, [key]: val }));
  };

  const updateTotalQuestions = (val) => {
    const newCount = parseInt(val, 10);
    if (isNaN(newCount) || newCount < 1) return;

    setFormData((prev) => {
      const currentQuestions = prev.questions;
      let newQuestions;

      if (newCount > currentQuestions.length) {
        const questionsToAdd = newCount - currentQuestions.length;
        const newItems = Array(questionsToAdd)
          .fill(null)
          .map(() => ({
            questionEn: "",
            questionJa: "",
            mediaUrl: "",
            options: Array(4)
              .fill(null)
              .map(() => ({ optionEn: "", optionJa: "" })),
            correctAnswerIndex: 0,
          }));
        newQuestions = [...currentQuestions, ...newItems];
      } else {
        newQuestions = currentQuestions.slice(0, newCount);
      }

      return {
        ...prev,
        totalQuestions: newCount,
        questions: newQuestions,
      };
    });
  };

  const updateQuestion = (idx, field, val) => {
    setFormData((prev) => {
      const newQ = [...prev.questions];
      newQ[idx] = { ...newQ[idx], [field]: val };
      return { ...prev, questions: newQ };
    });
  };

  const updateOption = (qIdx, oIdx, field, val) => {
    setFormData((prev) => {
      const newQ = [...prev.questions];
      newQ[qIdx].options[oIdx][field] = val;
      return { ...prev, questions: newQ };
    });
  };

  useEffect(() => {
    if (isEditMode) fetchMegaQuizData();
  }, [isEditMode]);

  const fetchMegaQuizData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      const res = await fetch(
        `${API_BASE_URL}/admin/mega-quizzes/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const result = await res.json();
      if (result.success) {
        const data = result.data;
        setFormData({
          titleEn: data.titleEn,
          titleJa: data.titleJa,
          coverImg: data.coverImg || "",
          startTime: data.startTime
            ? new Date(data.startTime).toISOString().slice(0, 16)
            : "",
          endTime: data.endTime
            ? new Date(data.endTime).toISOString().slice(0, 16)
            : "",
          totalQuestions: data.totalQuestions || 50,
          pointsPerQuestion: data.pointsPerQuestion || 1,
          questions: data.questions.map((q) => ({
            questionEn: q.questionEn,
            questionJa: q.questionJa,
            mediaUrl: q.mediaUrl || "",
            options: q.options.map((o) => ({
              optionEn: o.optionEn,
              optionJa: o.optionJa,
            })),
            correctAnswerIndex: q.options.findIndex(
              (o) => o.megaOptionId === q.answerId,
            ),
          })),
        });
      }
    } catch (e) {
      setError("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("adminToken");

      const formatToISOWithOffset = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toISOString();
      };

      const submissionData = {
        ...formData,
        startTime: formData.startTime
          ? new Date(formData.startTime).toISOString()
          : null,
        endTime: formData.endTime
          ? new Date(formData.endTime).toISOString()
          : null,
      };

      const url = isEditMode
        ? `${API_BASE_URL}/admin/mega-quizzes/${id}`
        : `${API_BASE_URL}/admin/mega-quizzes`;

      const method = isEditMode ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      if (data.success) {
        setSuccess("Mega Quiz saved successfully!");
        setTimeout(() => {
          navigate("/admin/dashboard/mega-quiz-management");
        }, 500);
      } else {
        setError(data.error || "Failed to save quiz");
        console.error("Submission error:", data);
        alert(data.error || "Submission failed");
      }
    } catch (err) {
      console.error(err);
      setError("Network error occurred");
      alert("Network error occurred");
    } finally {
      setLoading(false);
    }
  };

  const questionsPerStep = 10;
  const totalSteps = Math.ceil(formData.questions.length / questionsPerStep);

  return (
    <Box>
      <Group justify="space-between" mb="xl">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            {isEditMode ? "Edit Mega Quiz" : "Create Mega Quiz"}
          </Text>
          <Text size="sm" c="dimmed">
            Configure event details and questions
          </Text>
        </Box>
      </Group>

      {/* Stepper Navigation */}
      <Stepper
        active={currentStep}
        onStepClick={setCurrentStep}
        mb="xl"
        size="sm"
      >
        <Stepper.Step label="Basic Info" description="Event settings" />
        {Array.from({ length: totalSteps }).map((_, i) => (
          <Stepper.Step
            key={i}
            label={`Q ${i * 10 + 1}-${Math.min((i + 1) * 10, formData.questions.length)}`}
          />
        ))}
        <Stepper.Step label="Review" description="Confirm & Save" />
      </Stepper>

      {/* STEP 0: BASIC INFO */}
      {currentStep === 0 && (
        <Paper p="xl" withBorder radius="md">
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="Title (English)"
                required
                value={formData.titleEn}
                onChange={(e) => updateFormData("titleEn", e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Title (Japanese)"
                required
                value={formData.titleJa}
                onChange={(e) => updateFormData("titleJa", e.target.value)}
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <TextInput
                type="datetime-local"
                label="Start Time"
                value={formData.startTime}
                onChange={(e) => updateFormData("startTime", e.target.value)}
                leftSection={<IconCalendar size={16} />}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                type="datetime-local"
                label="End Time"
                value={formData.endTime}
                onChange={(e) => updateFormData("endTime", e.target.value)}
                leftSection={<IconCalendar size={16} />}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <Text size="sm" fw={500} mb={4}>
                Cover Image
              </Text>
              <ImageUpload
                onUpload={(url) => updateFormData("coverImg", url)}
                existingUrl={formData.coverImg}
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <NumberInput
                label="Total Questions"
                value={formData.totalQuestions}
                onChange={updateTotalQuestions}
                min={1}
                max={200}
                allowNegative={false}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label="Points per Question"
                value={formData.pointsPerQuestion}
                onChange={(v) => updateFormData("pointsPerQuestion", v)}
              />
            </Grid.Col>
          </Grid>
        </Paper>
      )}

      {/* QUESTION STEPS */}
      {currentStep > 0 && currentStep <= totalSteps && (
        <Stack gap="xl">
          {formData.questions
            .slice(
              (currentStep - 1) * questionsPerStep,
              currentStep * questionsPerStep,
            )
            .map((q, i) => {
              const actualIndex = (currentStep - 1) * questionsPerStep + i;
              return (
                <Paper
                  key={actualIndex}
                  withBorder
                  p="lg"
                  radius="md"
                  className="relative"
                >
                  <Badge className="absolute top-4 right-4" size="lg">
                    Question {actualIndex + 1}
                  </Badge>

                  <Grid mb="md">
                    <Grid.Col span={6}>
                      <Textarea
                        label="Question (EN)"
                        minRows={2}
                        value={q.questionEn}
                        onChange={(e) =>
                          updateQuestion(
                            actualIndex,
                            "questionEn",
                            e.target.value,
                          )
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Textarea
                        label="Question (JA)"
                        minRows={2}
                        value={q.questionJa}
                        onChange={(e) =>
                          updateQuestion(
                            actualIndex,
                            "questionJa",
                            e.target.value,
                          )
                        }
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Text size="xs" fw={500} mb={2}>
                        Media Attachment (Optional)
                      </Text>
                      <ImageUpload
                        onUpload={(url) =>
                          updateQuestion(actualIndex, "mediaUrl", url)
                        }
                        existingUrl={q.mediaUrl}
                        className="w-full"
                      />
                    </Grid.Col>
                  </Grid>

                  <Divider label="Answers" labelPosition="left" mb="sm" />

                  <Stack gap="xs">
                    {q.options.map((opt, oIdx) => (
                      <Group key={oIdx} grow align="start">
                        <Radio
                          checked={q.correctAnswerIndex === oIdx}
                          onChange={() =>
                            updateQuestion(
                              actualIndex,
                              "correctAnswerIndex",
                              oIdx,
                            )
                          }
                          label={`Option ${oIdx + 1}`}
                          className="mt-3"
                        />
                        <TextInput
                          placeholder="English Option"
                          value={opt.optionEn}
                          onChange={(e) =>
                            updateOption(
                              actualIndex,
                              oIdx,
                              "optionEn",
                              e.target.value,
                            )
                          }
                        />
                        <TextInput
                          placeholder="Japanese Option"
                          value={opt.optionJa}
                          onChange={(e) =>
                            updateOption(
                              actualIndex,
                              oIdx,
                              "optionJa",
                              e.target.value,
                            )
                          }
                        />
                      </Group>
                    ))}
                  </Stack>
                </Paper>
              );
            })}
        </Stack>
      )}

      {/* REVIEW STEP */}
      {currentStep === totalSteps + 1 && (
        <Paper p="xl" withBorder radius="md">
          <Text size="xl" fw={700} mb="md">
            Review Configuration
          </Text>
          <Grid>
            <Grid.Col span={4}>
              <Text c="dimmed">Title:</Text>{" "}
              <Text fw={600}>{formData.titleEn}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text c="dimmed">Start:</Text>{" "}
              <Text fw={600}>
                {new Date(formData.startTime).toLocaleString()}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text c="dimmed">Questions:</Text>{" "}
              <Text fw={600}>{formData.totalQuestions}</Text>
            </Grid.Col>
          </Grid>
          <Alert color="blue" mt="xl" icon={<IconCheck />}>
            Ready to launch? This will create a live Mega Quiz event.
          </Alert>
        </Paper>
      )}

      {/* Actions */}
      <Group justify="space-between" mt="xl">
        <Button
          variant="default"
          onClick={() => setCurrentStep((c) => Math.max(0, c - 1))}
        >
          Back
        </Button>
        {currentStep === totalSteps + 1 ? (
          <Button
            onClick={handleSubmit}
            loading={loading}
            color="purple"
            size="md"
          >
            Submit Mega Quiz
          </Button>
        ) : (
          <Button
            onClick={() =>
              setCurrentStep((c) => Math.min(totalSteps + 1, c + 1))
            }
            color="blue"
          >
            Next Step
          </Button>
        )}
      </Group>
    </Box>
  );
}
